import type { IconType } from '@evisort/eds/types/SharedIconProps';

/**
 * EDS 1.0 → 2.0 icon renames only. Keys that share a name pass through in
 * `toEdsIcon` — no entry needed when 1.0 and 2.0 use the same key.
 */
const EDS1_TO_EDS2_ICON: Record<string, IconType> = {
  ai: 'ai-sparkle',
  'ai-filled': 'ai-sparkle',
  'ai-loading-sparkle': 'loading',
  'ai-review': 'comment',
  analyzer: 'search',
  'arrow-back': 'arrow-left',
  'arrow-down': 'caret-down',
  'arrow-forward': 'arrow-right',
  'arrow-previous': 'arrow-left',
  'arrow-up': 'caret-up',
  automation: 'ai-custom-automation',
  'automation-gradient': 'ai-custom-automation',
  bars: 'left-panel-open',
  'book-sparkle': 'library',
  'bookmark-filled': 'bookmark',
  briefcase: 'work-briefcase',
  chatbot: 'add-new-conversation',
  checklist: 'checklist-approval',
  'chevron-up': 'caret-up',
  chip: 'warning-exclamation',
  'circle-check': 'check-circle',
  'circle-stop': 'pause',
  clipboard: 'copy',
  'cloud-download': 'download',
  'collapse-left': 'left-panel-close',
  'collapse-right': 'left-panel-open',
  compress: 'collapse-chevron',
  dash: 'minus-remove',
  department: 'buildings',
  dev: 'code',
  'double-arrow-left': 'chevron-left',
  'double-arrow-right': 'chevron-right',
  drive: 'folder',
  'evisort-ai': 'evisort-ai-static',
  'evisort-ai-animated': 'evisort-ai-static',
  expand: 'expand-chevron',
  expiring: 'history-activity-log',
  field: 'fields',
  'field-address': 'address-location',
  'field-boolean': 'boolean',
  'field-clause': 'clause',
  'field-date': 'calendar-today',
  'field-dropdown': 'dropdown',
  'field-email': 'email',
  'field-file': 'document',
  'field-folder': 'folder',
  'field-monetary': 'money',
  'field-multiple-choice': 'dropdown',
  'field-number': 'number',
  'field-paragraph': 'text-area',
  'field-percentage': 'percentage',
  'field-short-response': 'text-input',
  'field-time-period': 'calendar-clock',
  file: 'document',
  'file-magnifying-glass': 'search',
  'file-pen': 'edit',
  'file-powerpoint': 'file-slide',
  'file-spreadsheet': 'file-sheet',
  flag: 'stage-flag',
  format: 'format-settings',
  'format-align-center': 'align-center',
  'format-align-justify': 'align-justify',
  'format-align-left': 'align-left',
  'format-align-right': 'align-right',
  'format-blockquote': 'indent',
  'format-bold': 'bold',
  'format-bulleted-list': 'list-unordered-bullet',
  'format-code': 'code',
  'format-code-block': 'code',
  'format-heading-1': 'heading-1',
  'format-heading-2': 'heading-2',
  'format-italic': 'italic',
  'format-numbered-list': 'list-ordered-numbered',
  'format-strikethrough': 'strikethrough',
  'format-underline': 'underline',
  gear: 'settings-gear',
  globe: 'globe-language',
  groups: 'group',
  highlight: 'highlight-color',
  'left-panel': 'left-panel-open',
  launch: 'open-external-link',
  layout: 'dashboard',
  lightbulb: 'info',
  'loading-content': 'loading',
  'loading-content-deprecated': 'loading',
  'loading-dashed': 'loading',
  lock: 'lock-key-hole',
  maximize: 'expand-chevron',
  message: 'comment',
  mic: 'microphone',
  minimize: 'collapse-chevron',
  mosaic: 'dashboard',
  'nav-panel': 'left-panel-open',
  'new-conversation': 'add-new-conversation',
  'not-visible': 'invisible',
  'open-in-new': 'open-external-link',
  panel: 'left-panel-open',
  panels: 'left-panel-open',
  'plus-circle': 'plus',
  'push-pin': 'pin',
  'push-pin-filled': 'pin',
  question: 'help',
  'reaction-thumbsdown': 'dislike-thumb-down',
  'reaction-thumbsdown-selected': 'dislike-thumb-down',
  'reaction-thumbsup': 'like-thumb-up',
  'reaction-thumbsup-selected': 'like-thumb-up',
  reauthenticate: 'reload',
  reconnect: 'sync-problem-reconnect',
  remove: 'minus-remove',
  reorder: 'drag-and-drop-indicator',
  report: 'document',
  rerun: 'reload',
  restore: 'reload',
  review: 'comment',
  'save-as-version': 'save-as',
  'search-gradient': 'search',
  sequential: 'sort',
  'sparkle-single-small': 'ai-sparkle',
  'status-active': 'check',
  'status-ai': 'ai-sparkle',
  'status-alert': 'warning-exclamation',
  'status-current': 'info',
  'status-danger': 'warning-exclamation',
  'status-inactive': 'info',
  'status-indicator-danger': 'warning-exclamation',
  'status-indicator-success': 'check-circle',
  'status-indicator-warning': 'warning-exclamation',
  'status-info': 'info',
  'status-new': 'info',
  'status-new-gradient': 'info',
  'status-not-started': 'info',
  'status-optional': 'info',
  'status-pending': 'loading',
  'status-reject': 'warning-exclamation',
  'status-complete': 'check-circle',
  'status-success': 'check-circle',
  'status-warning': 'warning-exclamation',
  tag: 'bookmark',
  'task-complete': 'checklist-approval',
  'turn-tracking': 'calendar-clock',
  'trash-permanent': 'trash',
  view: 'visible',
  'workflow-routing-agent': 'workflow',
  wrench: 'settings-gear',
  'workday-dub': 'workday',
};

export function toEdsIcon(icon: string): IconType {
  return EDS1_TO_EDS2_ICON[icon] ?? (icon as IconType);
}
